import React, { useCallback, useEffect, useState } from 'react';
import {
    WxbAlert,
    WxbBadge,
    WxbButton,
    WxbEmpty,
    WxbIcon,
    WxbModal,
    WxbSpinner,
    wxbToast,
} from '../wxb-ui';
import { batchPlanApi, type BatchRefreshPreview } from '../../services/api';
import type { BatchPlan } from '../../types';
import {
    canReplaceBatchTemplate,
    hasRelatedBatchRefreshChanges,
} from './batchRefreshUtils';

interface BulkRefreshModalProps {
    visible: boolean;
    batches: BatchPlan[];   // only DRAFT batches
    onClose: () => void;
    onApplied: () => void;
}

interface PreviewItem {
    batch: BatchPlan;
    preview: BatchRefreshPreview | null;
    error: string | null;
}

const REFRESH_CONCURRENCY = 4;

const mapWithConcurrency = async <T, R,>(
    values: T[],
    limit: number,
    mapper: (value: T, index: number) => Promise<R>,
): Promise<R[]> => {
    const results = new Array<R>(values.length);
    let nextIndex = 0;
    const workers = Array.from({ length: Math.min(limit, values.length) }, async () => {
        while (nextIndex < values.length) {
            const index = nextIndex;
            nextIndex += 1;
            results[index] = await mapper(values[index], index);
        }
    });
    await Promise.all(workers);
    return results;
};

const BulkRefreshModal: React.FC<BulkRefreshModalProps> = ({
    visible,
    batches,
    onClose,
    onApplied,
}) => {
    const [items, setItems] = useState<PreviewItem[]>([]);
    const [loadingPreviews, setLoadingPreviews] = useState(false);
    const [applying, setApplying] = useState(false);

    useEffect(() => {
        if (!visible || batches.length === 0) return;

        let cancelled = false;
        setLoadingPreviews(true);
        setItems(batches.map((b) => ({ batch: b, preview: null, error: null })));

        mapWithConcurrency(
            batches,
            REFRESH_CONCURRENCY,
            (batch) =>
                batchPlanApi
                    .refreshPreview(batch.id)
                    .then((preview) => ({ batch, preview, error: null } satisfies PreviewItem))
                    .catch((err) => ({
                        batch,
                        preview: null,
                        error: err?.response?.data?.error || '对比失败',
                    } satisfies PreviewItem)),
        ).then((results) => {
            if (!cancelled) {
                setItems(results);
                setLoadingPreviews(false);
            }
        });

        return () => { cancelled = true; };
    }, [visible, batches]);

    // 完整替换也承担历史残留修复，因此“无预览差异”的草稿批次同样可执行。
    const replaceable = items.filter(
        (item) => item.preview && canReplaceBatchTemplate(item.preview),
    );

    const handleApply = useCallback(async () => {
        if (replaceable.length === 0) return;
        setApplying(true);
        const results = await mapWithConcurrency(
            replaceable,
            REFRESH_CONCURRENCY,
            async (item) => {
                const p = item.preview!;
                try {
                    await batchPlanApi.refresh(item.batch.id, {
                        previewRevision: p.revision,
                    });
                    return { ok: true as const, batchCode: item.batch.batch_code };
                } catch (error: any) {
                    return {
                        ok: false as const,
                        batchCode: item.batch.batch_code,
                        error: error?.response?.data?.error || '刷新失败',
                    };
                }
            },
        );
        setApplying(false);

        const succeeded = results.filter((result) => result.ok).length;
        const failed = results.length - succeeded;
        if (failed === 0) {
            wxbToast.success(`已为 ${succeeded} 个批次重新替换模版`);
        } else {
            const firstFailure = results.find((result) => !result.ok);
            wxbToast.warning(
                `${succeeded} 个成功，${failed} 个失败`
                + (firstFailure && !firstFailure.ok
                    ? `；${firstFailure.batchCode}：${firstFailure.error}`
                    : ''),
            );
        }
        onApplied();
    }, [replaceable, onApplied]);

    const footer = (
        <div className="batch-refresh-modal__footer">
            <WxbButton
                variant="ghost"
                onClick={onClose}
            >
                {replaceable.length > 0 ? '取消' : '关闭'}
            </WxbButton>
            {replaceable.length > 0 && (
                <WxbButton
                    variant="primary"
                    onClick={handleApply}
                    disabled={applying || loadingPreviews}
                >
                    {applying ? '替换中...' : `全部替换 (${replaceable.length} 个批次)`}
                </WxbButton>
            )}
        </div>
    );

    return (
        <WxbModal
            open={visible}
            title="批量重新替换模版"
            onCancel={onClose}
            footer={footer}
            width={560}
            centered
        >
            <div className="batch-refresh-modal__content">
                {loadingPreviews && (
                    <WxbSpinner tip={`正在对比 ${batches.length} 个批次的模版差异...`} />
                )}

                {!loadingPreviews && items.length === 0 && (
                    <WxbEmpty description="没有可刷新的批次" />
                )}

                {!loadingPreviews && items.length > 0 && (
                    <>
                        <WxbAlert
                            title="每个批次都将执行完整替换"
                            className="batch-refresh-modal__alert"
                        >
                            即使预览无差异，也会清空每个批次的模板派生快照、共享组覆盖及其人工共享关系，
                            再按当前模板重建。
                        </WxbAlert>
                        <div className="batch-refresh-modal__bulk-summary">
                            已选 {batches.length} 个草稿批次，其中{' '}
                            <strong>{replaceable.length} 个</strong>{' '}
                            可按当前模版完整替换。
                        </div>
                        {items.map((item) => {
                            const s = item.preview?.summary;
                            const changeCount = s ? s.added + s.removed + s.changed : 0;
                            const blocked = item.preview ? !item.preview.canRefresh : false;
                            const hasError = !!item.error;
                            const relatedChanges = item.preview
                                ? hasRelatedBatchRefreshChanges(item.preview)
                                : false;

                            return (
                                <div key={item.batch.id} className="batch-refresh-modal__bulk-row">
                                    <WxbIcon
                                        name="batch-record"
                                        size={16}
                                        className="batch-refresh-modal__bulk-icon"
                                    />
                                    <div className="batch-refresh-modal__bulk-main">
                                        <div className="batch-refresh-modal__bulk-code">
                                            {item.batch.batch_code}
                                        </div>
                                        {item.batch.day0_date && (
                                            <div className="batch-refresh-modal__bulk-date">
                                                Day0 {item.batch.day0_date}
                                            </div>
                                        )}
                                    </div>
                                    <div className="batch-refresh-modal__bulk-status">
                                        {hasError && (
                                            <WxbBadge status="error" variant="bar" label={item.error!} />
                                        )}
                                        {!hasError && blocked && (
                                            <WxbBadge status="warning" variant="bar" label={item.preview!.blockReason || '不可刷新'} />
                                        )}
                                        {!hasError && !blocked && changeCount === 0 && !relatedChanges && (
                                            <WxbBadge status="neutral" variant="bar" label="完整重建" />
                                        )}
                                        {!hasError && !blocked && (changeCount > 0 || relatedChanges) && (
                                            <WxbBadge
                                                status="info"
                                                variant="bar"
                                                label={[
                                                    s!.added > 0 && `新增 ${s!.added}`,
                                                    s!.changed > 0 && `变更 ${s!.changed}`,
                                                    s!.removed > 0 && `移除 ${s!.removed}`,
                                                    item.preview!.relatedChanges?.constraints && '约束',
                                                    item.preview!.relatedChanges?.shareGroups && '共享组',
                                                    item.preview!.relatedChanges?.resourceRules && '资源规则',
                                                ].filter(Boolean).join(' · ')}
                                            />
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </>
                )}
            </div>
        </WxbModal>
    );
};

export default BulkRefreshModal;
